
import { Options, Vue } from "vue-class-component";
import MButton from "@/components/MButton.vue";
import MIcon from "@/components/MIcon.vue";
import MLoadingIcon from "@/components/MLoadingIcon.vue";
import MTable from "@/components/MTable.vue";
import MNavBar from "@/components/MNavBar.vue";
import MHeaderPanel from "@/components/MHeaderPanel.vue";
import MScAddModal from "@/components/school/MScAddModal.vue";
import MscEditModal from "@/components/school/MscEditModal.vue";
import { School, schoolCollectionKey, SchoolConfig } from "@/entities/school";
import { getTutor, getTutorDetailsOfSchools } from "@/api/tutor";
import { Tutor } from "@/entities/tutor";
import firebase from "firebase/app";
import store from "@/store";
import { deleteChildSchoolfromParent, deleteSchool } from "@/api/school";
import { Target } from "@/entities/target";
import { Getter, State } from "@/store/helper";
import { saveErrorLog } from "@/api/error";
import {
  convertToSchoolTableData,
  schoolsTableDefinition
} from "@/utils/table";
import { escapeCsvField, exportToUTF8Csv } from "@/utils/csv";
import { getLoginTutorsOfSchool } from "@/api/user";
import { User } from "@/entities/user";
import dayjs from "dayjs";
import { getAuthorityMap } from "@/utils/tutor";

@Options({
  components: {
    MButton,
    MIcon,
    MLoadingIcon,
    MTable,
    MNavBar,
    MHeaderPanel,
    MScAddModal,
    MscEditModal
  },
  watch: {
    accessibleSchools: function() {
      if (this.accessibleSchools.length > 0) {
        this.updateSchoolDetails();
      }
    }
  }
})
export default class SchoolSettings extends Vue {
  isDisplayedAddSchoolModal = false;
  isDisplayedEditSchoolModal = false;
  searchKeyword = "";
  load = false;
  selectedId = "";
  selectedIds: string[] = [];
  schoolList: { [key: string]: string | number | boolean }[] = [];
  definitions = schoolsTableDefinition;
  tutors: User[] = [];

  @State("schools") schools!: School[];
  @State("schoolsForServieProvider") schoolsForServieProvider?: School[];
  @State("schoolConfigs") schoolConfigs!: SchoolConfig[];
  @State("schoolConfig") schoolConfig!: SchoolConfig;
  @State("targets") targets!: Target[];
  @Getter("isSuperAdmin") isSuperAdmin!: boolean;
  @Getter("isServiceProvider") isServiceProvider!: boolean;
  @Getter("isAdmin") isAdmin!: boolean;

  get isSchool(): boolean {
    return store.state.isSchool;
  }

  get accessibleSchools(): School[] {
    if (this.isSuperAdmin) {
      return this.schools;
    } else if (this.isServiceProvider) {
      return this.schoolsForServieProvider as School[];
    }
    return [];
  }

  get selectedSchool(): School | null {
    const matchSchools = this.accessibleSchools.filter(
      item => item.ref.id === this.selectedId
    );
    if (matchSchools.length === 0) {
      return null;
    }
    return matchSchools[0];
  }

  get selectedSchoolConfig(): SchoolConfig | null {
    const matchSchoolConfigs = this.schoolConfigs.filter(
      item => item.ref.id === this.selectedId
    );
    if (matchSchoolConfigs.length === 0) {
      return null;
    }
    return matchSchoolConfigs[0];
  }

  get filteredSchools(): { [key: string]: string | number | boolean }[] {
    if (this.searchKeyword.length === 0) {
      return this.schoolList;
    }

    return this.schoolList.filter(
      data =>
        data?.schoolId
          .toString()
          .toLowerCase()
          .includes(this.searchKeyword.toLowerCase()) ||
        data?.name
          .toString()
          .toLowerCase()
          .includes(this.searchKeyword.toLowerCase()) ||
        data?.owner
          .toString()
          .toLowerCase()
          .includes(this.searchKeyword.toLowerCase())
    );
  }

  get selectedTutors(): User[] {
    return this.tutors.filter(tutor => tutor.schoolId === this.selectedId);
  }

  showAddModal() {
    this.isDisplayedAddSchoolModal = true;
  }

  async showEditModal(id: string) {
    this.selectedId = id;
    this.isDisplayedEditSchoolModal = true;
    this.tutors = await getLoginTutorsOfSchool(id);
  }

  async onEditModalClose() {
    this.isDisplayedEditSchoolModal = false;
    await Promise.all([
      store.dispatch("getSchools"),
      store.dispatch("getSchoolConfigs")
    ]);
  }

  changeSelectedId(id: string) {
    if (this.selectedIds.includes(id)) {
      this.selectedIds = this.selectedIds.filter(item => item !== id);
    } else {
      this.selectedIds.push(id);
    }
  }

  async deleteSchool() {
    if (this.selectedIds.length !== 1) {
      return;
    }
    await store.commit("START_LOADING", "招待中生徒情報移行中...");
    const schoolConfigRef: firebase.firestore.DocumentReference<
      firebase.firestore.DocumentData
    > | null = this.schoolConfig?.ref ?? null;

    const idToDelete = this.selectedIds[0];
    try {
      await deleteChildSchoolfromParent(
        this.isServiceProvider,
        schoolConfigRef,
        idToDelete
      );
      await deleteSchool(
        firebase
          .firestore()
          .collection(schoolCollectionKey)
          .doc(idToDelete)
      );
      await store.commit("END_LOADING");
      alert("導入スクールの削除に成功しました");
      this.$router.go(0);
    } catch (e) {
      store.commit("END_LOADING");
      alert(e);
      await saveErrorLog(
        store.state.role,
        e.code,
        e.message,
        "Failed to delete school"
      );
      return;
    }
  }

  created() {
    this.updateSchoolDetails();
    if (this.isServiceProvider) {
      this.definitions = this.definitions.filter(
        _ =>
          _.key !== "allowUseOfCustomOfficialLine" &&
          _.key !== "hideMingakuStudyRoom" &&
          _.key !== "schoolAiUsageExpiresAt" &&
          _.key !== "subscriptionType" &&
          _.key !== "freeSubscriptionStartDate" &&
          _.key !== "freeSubscriptionEndDate" &&
          _.key !== "paidSubscriptionStartDate" &&
          _.key !== "paidSubscriptionEndDate" &&
          _.key !== "prefecture" &&
          _.key !== "schoolCategory"
      );
    }
  }

  async updateSchoolDetails() {
    this.load = true;
    const owners = await this.getOwnerOfSchools(this.accessibleSchools);
    const sortedSchools = [...this.accessibleSchools];
    sortedSchools.sort((a, b) => b.data.id.localeCompare(a.data.id));
    this.load = false;

    this.schoolList = [];
    let index = 0;
    for (const school of sortedSchools) {
      this.schoolList.push(
        await convertToSchoolTableData(
          school,
          owners,
          this.schoolConfigs,
          this.targets
        )
      );
      index++;
      if (index % 40 === 0) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
  }

  async getOwnerOfSchools(schools: School[]) {
    const owners = await Promise.all(
      schools
        .filter(school => school.data.owner !== null)
        .map(school => getTutor(school.data.owner!))
    );
    return owners.filter((owner): owner is Tutor => owner !== null);
  }

  exportSchoolDetailsToCsv(
    option: { exportAll: boolean } = { exportAll: false }
  ) {
    const selectedSchoolList = option.exportAll
      ? [...this.schoolList]
      : this.schoolList.filter(s => this.selectedIds.some(_ => _ === s.id));
    if (selectedSchoolList.length === 0) {
      alert("出力するスクールがありません。");
      return;
    }

    const accessibleSchoolIds = this.accessibleSchools.map(_ => _.ref.id);
    if (
      !selectedSchoolList.every(s =>
        accessibleSchoolIds.includes(s.id as string)
      )
    ) {
      alert("エクスポートする権限がないスクールが含まれています。");
      return;
    }
    const headerRow = this.definitions.map(d => d.text).join(",") + "\n";
    const csvData = [...selectedSchoolList]
      .sort((a, b) => {
        return +a.schoolId - +b.schoolId;
      })
      .reduce((acc: string, cur) => {
        const row = [];
        row.push(cur.schoolId);
        row.push(cur.name);
        row.push(cur.owner);
        row.push(cur.email);
        row.push(cur.classroomNum);
        row.push(cur.studentNum);
        row.push(cur.tutorNum);
        row.push(cur.paymentType);
        row.push(cur.allowCreateRoom);
        row.push(cur.activateTime);
        if (!this.isServiceProvider) row.push(cur.schoolAiUsageExpiresAt);
        row.push(cur.target);
        row.push(cur.status);
        row.push(cur.role);
        row.push(cur.useSchoolAiExclusively);
        if (!this.isServiceProvider) row.push(cur.allowUseOfCustomOfficialLine);
        row.push(cur.hideLoginInvitation);
        if (!this.isServiceProvider) row.push(cur.hideMingakuStudyRoom);
        row.push(cur.allowStudentsToChangePassword);
        if (!this.isServiceProvider) {
          row.push(cur.subscriptionType);
          row.push(cur.freeSubscriptionStartDate);
          row.push(cur.freeSubscriptionEndDate);
          row.push(cur.paidSubscriptionStartDate);
          row.push(cur.paidSubscriptionEndDate);
          row.push(cur.prefecture);
          row.push(cur.schoolCategory);
        }
        return acc + row.join(",") + "\n";
      }, headerRow);

    exportToUTF8Csv(
      csvData,
      `スクールデータ_${dayjs().format("YYYY-MM-DD")}現在`
    );
  }

  async exportTutorDetailsToCsv() {
    if (!this.isAdmin) return;

    if (this.selectedIds.length === 0) {
      alert("対象データがありません。");
      return;
    }

    try {
      store.commit("START_LOADING", "スタッフ情報取得中...");
      const res = await getTutorDetailsOfSchools({
        schoolIds: this.selectedIds
      });

      // 教室の最大数を取得
      const maxClassrooms = res.reduce((max, school) => {
        return Math.max(
          max,
          ...school.tutorDetailsWithClassrooms.map(t => t.classroomNames.length)
        );
      }, 0);

      // ヘッダー行を作成
      const headerColumns = [
        "スクール名",
        "スタッフ名",
        "権限",
        "Googleアカウント",
        "Microsoftアカウント"
      ];
      for (let i = 1; i <= maxClassrooms; i++) {
        headerColumns.push(`教室${i}`);
      }
      const headerRow = headerColumns.join(",") + "\n";
      // データ行を作成
      const dataRows = res.flatMap(school =>
        school.tutorDetailsWithClassrooms.map(tutor => {
          const row = [
            escapeCsvField(school.schoolName),
            escapeCsvField(tutor.name),
            escapeCsvField(
              getAuthorityMap({ isSchool: school.isSchool ?? false })[
                tutor.authority
              ]
            ),
            escapeCsvField(tutor.gmail),
            escapeCsvField(tutor.microsoftEmail)
          ];
          // 教室を追加（最大数に合わせてパディング）
          for (let i = 0; i < maxClassrooms; i++) {
            row.push(escapeCsvField(tutor.classroomNames[i] ?? ""));
          }
          return row.join(",");
        })
      );

      const csvData = headerRow + dataRows.join("\n");

      exportToUTF8Csv(
        csvData,
        `スタッフデータ_${dayjs().format("YYYY-MM-DD")}現在`
      );
    } catch (e) {
      console.error(e);
      alert(
        "スタッフ情報の取得に失敗しました。" +
          (e instanceof Error ? e.message : "")
      );
    } finally {
      store.commit("END_LOADING");
    }
  }
}
